import { useEffect, useState } from "react";

// F33.3 — detect when a newer admin bundle is live on origin and the
// current tab is running the previous one. Compares the bundle's baked-in
// __BUILD_TIMESTAMP__ (set by vite.config.ts at build time) against the
// `buildTs` field served at /version.json (written by the same vite
// plugin into dist/version.json on every build).
//
// Background context: shared-api / global-entities / admin SPA deploy on
// independent workflows. Today operators saw the v1 URL-only Logo input
// long after the F33.1 file-upload backend was live, because the admin
// SPA bundle (deployed separately) hadn't rebuilt yet. This hook surfaces
// that delta as a visible banner instead of "hard refresh" tribal
// knowledge.

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const VERSION_JSON_URL = "/version.json";

export function useFreshBundle(): boolean {
	const [isStale, setIsStale] = useState(false);

	useEffect(() => {
		let cancelled = false;
		const check = async () => {
			try {
				const res = await fetch(`${VERSION_JSON_URL}?ts=${Date.now()}`, {
					cache: "no-store",
				});
				if (!res.ok) return;
				const data = (await res.json()) as { buildTs?: string };
				if (
					!cancelled &&
					data.buildTs &&
					typeof __BUILD_TIMESTAMP__ === "string" &&
					data.buildTs !== __BUILD_TIMESTAMP__
				) {
					setIsStale(true);
				}
			} catch {
				// Network blip / dev server doesn't serve version.json —
				// quietly ignore; next tick will retry.
			}
		};
		// Run once on mount (covers the case where the tab was open through
		// a deploy and the user comes back to it; no need to wait the full
		// 5 min before nagging).
		void check();
		const id = window.setInterval(check, POLL_INTERVAL_MS);
		return () => {
			cancelled = true;
			window.clearInterval(id);
		};
	}, []);

	return isStale;
}
